// **Der Wizard „Neuer Mandant"** (docs/11 11) — sieben Schritte, jeder
// speichert einen Entwurf, keine Infrastruktur. Man kann ihn verlassen und
// fortsetzen (die Liste führt einen Entwurf zurück an seinen Schritt). Erst der
// siebte stößt die Einrichtung an.

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, post, put } from "../../api/client";
import { useCatalog, useHost, useTenant } from "../../api/queries";
import type { Checks, StepCheck, StepSaveResponse, Tenant } from "../../api/types";
import { errorText, WIZARD_STEPS } from "../../lib/labels";
import { Link, useNavigate } from "../../router/router";
import { ErrorNote, Loading } from "../../ui/badge";
import { toast } from "../../ui/toaster";
import { hasErrors, storeSavedStep } from "../steps/cache";
import { StepEditor, type StepNumber } from "../steps/step-editor";
import type { StepBody } from "../steps/types";
import { StepsNav } from "./steps-nav";
import { Summary } from "./summary";
import { WizardHead } from "./wizard-head";

export function WizardView({ id, step }: { id: number | null; step: number }) {
  if (id === null) return <WizardStep tenant={null} checks={null} step={1} />;
  return <WizardLoad id={id} step={step} />;
}

function WizardLoad({ id, step }: { id: number; step: number }) {
  const q = useTenant(id);
  const navigate = useNavigate();
  const tenant = q.data?.tenant;
  // Ein eingerichteter Mandant hat keinen Wizard mehr — seine Detailseite.
  const notDraft = Boolean(tenant && tenant.state !== "draft");
  useEffect(() => { if (notDraft) navigate(`/tenants/${id}`, { replace: true }); }, [notDraft, id, navigate]);
  if (q.isError) return <ErrorNote error={q.error} />;
  // Erst die frische Antwort: Der Schritt baut seinen Zustand einmal aus ihr,
  // ein älterer Stand aus dem Cache bliebe sonst im Formular stehen.
  if (!q.data || !q.isFetchedAfterMount) return <Loading />;
  if (notDraft) return null;
  if (step === 7) return <Summary tenant={q.data.tenant} checks={q.data.checks} />;
  return <WizardStep tenant={q.data.tenant} checks={q.data.checks} step={step as StepNumber} />;
}

function WizardStep({ tenant: initial, checks, step }: { tenant: Tenant | null; checks: Checks | null; step: StepNumber }) {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [tenant, setTenant] = useState(initial);
  // Fehler erst, wenn der Schritt schon einmal abgeschickt wurde: Ein Schritt,
  // den man gerade zum ersten Mal öffnet, stand sonst voller roter „Pflichtfeld"
  // an Feldern, die noch niemand angefasst hat.
  const [lastCheck, setLastCheck] = useState<StepCheck | null>(
    initial && step < (initial.wizard_step ?? 1) ? checks?.[String(step)] ?? null : null,
  );
  const host = useHost(false, step === 2).data ?? null;

  /** Speichert den Schritt; das Prüfergebnis, wenn der Server ihn angenommen hat, sonst `null`. */
  const saveStep = async (body: StepBody): Promise<StepCheck | null> => {
    try {
      if (!tenant) {
        const res = await post<{ tenant: Tenant }>("/tenants", body);
        setLastCheck(null);
        navigate(`/tenants/${res.tenant.id}/wizard/2`, { replace: true });
        return null;
      }
      const res = await put<StepSaveResponse>(`/tenants/${tenant.id}/steps/${step}`, body);
      setTenant(res.tenant);
      setLastCheck(res.check);
      storeSavedStep(client, tenant.id, step, res);
      return res.check;
    } catch (err) {
      if (err instanceof ApiError && err.body?.check) setLastCheck(err.body.check);
      toast(errorText(err), "error");
      return null;
    }
  };

  const next = async (collect: () => StepBody) => {
    const check = await saveStep(collect());
    if (!check || !tenant) return;
    if (hasErrors(check)) { toast("Bitte die markierten Felder ergänzen", "error"); return; }
    navigate(`/tenants/${tenant.id}/wizard/${step + 1}`);
  };

  return (
    <>
      <WizardHead tenant={tenant} />
      <StepsNav tenant={tenant} current={step} />
      <StepEditor
        step={step} tenant={tenant} catalog={catalog} check={lastCheck} host={host}
        title={`${step}. ${WIZARD_STEPS[step - 1]}`}
        onSave={async (body) => (await saveStep(body)) !== null}
        foot={(collect) => (
          <>
            {step > 1 && tenant && <Link className="pf-btn" to={`/tenants/${tenant.id}/wizard/${step - 1}`}>Zurück</Link>}
            {tenant && <button type="button" className="pf-btn" onClick={async () => { if (await saveStep(collect())) toast("Gespeichert"); }}>Speichern</button>}
            <button type="button" className="pf-btn pf-btn--primary" onClick={() => void next(collect)}>Weiter</button>
          </>
        )}
      />
    </>
  );
}
